/**
 * Mordant Saturation Field visual overlay.
 *
 * When a combat encounter is a Mordant, render a chromatic overlay over the
 * arena: a soft vignette tinted in the dye color + drifting "motes" particles
 * that float upward through the scene. Bone uses bone-white tint with sparser,
 * skeletal-feeling motes.
 *
 * Combat math is already applied server-side via applyMordantField — this is
 * purely visual signal so the player reads the arena as a Mordant on sight.
 */

export type MordantColorId =
  | 'crimson' | 'verdant' | 'azure' | 'citrine' | 'pearl'
  | 'ochre' | 'slate' | 'bronze' | 'silver' | 'gilt'
  | 'pitch' | 'violet' | 'bone';

interface MordantPalette {
  /** Hex tint for vignette + motes. */
  tint: number;
  /** Vignette opacity at edges (0..1). */
  vignetteAlpha: number;
  /** Mote count per second emitted across the scene. */
  emissionRate: number;
  /** Mote shape — 'orb' for chromatic, 'shard' for Bone. */
  shape: 'orb' | 'shard';
}

/** Tints + emission rates per color. Bone is intentionally sparser + shard-shaped. */
const PALETTES: Record<MordantColorId, MordantPalette> = {
  crimson: { tint: 0xdc2626, vignetteAlpha: 0.22, emissionRate: 8, shape: 'orb' },
  verdant: { tint: 0x16a34a, vignetteAlpha: 0.20, emissionRate: 8, shape: 'orb' },
  azure:   { tint: 0x2563eb, vignetteAlpha: 0.22, emissionRate: 8, shape: 'orb' },
  citrine: { tint: 0xeab308, vignetteAlpha: 0.22, emissionRate: 10, shape: 'orb' },
  pearl:   { tint: 0xf8fafc, vignetteAlpha: 0.18, emissionRate: 12, shape: 'orb' },
  ochre:   { tint: 0xa16207, vignetteAlpha: 0.20, emissionRate: 6, shape: 'orb' },
  slate:   { tint: 0x64748b, vignetteAlpha: 0.20, emissionRate: 6, shape: 'orb' },
  bronze:  { tint: 0xb45309, vignetteAlpha: 0.20, emissionRate: 6, shape: 'orb' },
  silver:  { tint: 0xcbd5e1, vignetteAlpha: 0.18, emissionRate: 10, shape: 'orb' },
  gilt:    { tint: 0xfbbf24, vignetteAlpha: 0.22, emissionRate: 10, shape: 'orb' },
  pitch:   { tint: 0x1f2937, vignetteAlpha: 0.28, emissionRate: 8, shape: 'orb' },
  violet:  { tint: 0x9333ea, vignetteAlpha: 0.22, emissionRate: 8, shape: 'orb' },
  // Bone: bone-white tint + sparser shard-shaped motes (skeletal motif)
  bone:    { tint: 0xe7e5d0, vignetteAlpha: 0.18, emissionRate: 5, shape: 'shard' },
};

/** Texture key used for the mote particle. Generated lazily per shape. */
function moteTextureKey(shape: 'orb' | 'shard'): string {
  return `mordant-mote-${shape}`;
}

/** Texture key used for the vignette overlay. Single radial-gradient gen. */
const VIGNETTE_TEXTURE_KEY = 'mordant-vignette';

/**
 * Owns the Phaser display objects for an active Mordant Saturation Field.
 * Construct once per combat fight; call destroy() when the scene ends.
 */
export class MordantOverlay {
  private vignette: Phaser.GameObjects.Image | null = null;
  private emitter: Phaser.GameObjects.Particles.ParticleEmitter | null = null;
  private resizeBound = this.handleResize.bind(this);

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly color: MordantColorId,
  ) {
    const palette = PALETTES[color];
    this.ensureTextures(palette.shape);
    this.createVignette(palette);
    this.createEmitter(palette);
    this.scene.scale.on('resize', this.resizeBound);
  }

  /** Build the vignette + mote textures if not already cached on the scene. */
  private ensureTextures(shape: 'orb' | 'shard'): void {
    // Vignette: a radial-gradient texture (transparent center, opaque edges).
    if (!this.scene.textures.exists(VIGNETTE_TEXTURE_KEY)) {
      const size = 512;
      const canvas = this.scene.textures.createCanvas(VIGNETTE_TEXTURE_KEY, size, size);
      if (canvas) {
        const ctx = canvas.getContext();
        const gradient = ctx.createRadialGradient(size / 2, size / 2, size * 0.25, size / 2, size / 2, size * 0.6);
        gradient.addColorStop(0, 'rgba(255, 255, 255, 0)');
        gradient.addColorStop(1, 'rgba(255, 255, 255, 1)');
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, size, size);
        canvas.refresh();
      }
    }

    const moteKey = moteTextureKey(shape);
    if (!this.scene.textures.exists(moteKey)) {
      const size = 16;
      const g = this.scene.make.graphics({ x: 0, y: 0 }, false);
      g.fillStyle(0xffffff, 1);
      if (shape === 'orb') {
        g.fillCircle(size / 2, size / 2, size / 2);
      } else {
        // Shard: 4-pointed star (skeletal feel)
        g.beginPath();
        g.moveTo(size / 2, 0);
        g.lineTo(size * 0.62, size * 0.42);
        g.lineTo(size, size / 2);
        g.lineTo(size * 0.62, size * 0.58);
        g.lineTo(size / 2, size);
        g.lineTo(size * 0.38, size * 0.58);
        g.lineTo(0, size / 2);
        g.lineTo(size * 0.38, size * 0.42);
        g.closePath();
        g.fillPath();
      }
      g.generateTexture(moteKey, size, size);
      g.destroy();
    }
  }

  private createVignette(palette: MordantPalette): void {
    const w = this.scene.scale.width;
    const h = this.scene.scale.height;
    this.vignette = this.scene.add
      .image(w / 2, h / 2, VIGNETTE_TEXTURE_KEY)
      .setDisplaySize(w, h)
      .setTint(palette.tint)
      .setAlpha(palette.vignetteAlpha)
      .setBlendMode(Phaser.BlendModes.SCREEN)
      .setDepth(900)
      .setScrollFactor(0);
  }

  private createEmitter(palette: MordantPalette): void {
    const w = this.scene.scale.width;
    const h = this.scene.scale.height;
    const moteKey = moteTextureKey(palette.shape);

    this.emitter = this.scene.add.particles(0, 0, moteKey, {
      x: { min: 0, max: w },
      y: h + 20,
      lifespan: { min: 4500, max: 7000 },
      speedY: { min: -22, max: -45 },
      speedX: { min: -8, max: 8 },
      scale: { start: 0.6, end: 0 },
      alpha: { start: 0.55, end: 0 },
      tint: palette.tint,
      blendMode: Phaser.BlendModes.SCREEN,
      frequency: 1000 / palette.emissionRate,
      quantity: 1,
    });
    this.emitter.setDepth(905);
    this.emitter.setScrollFactor(0);
  }

  private handleResize(gameSize: Phaser.Structs.Size): void {
    const w = gameSize.width;
    const h = gameSize.height;
    if (this.vignette) {
      this.vignette.setPosition(w / 2, h / 2).setDisplaySize(w, h);
    }
    if (this.emitter) {
      // Update emitter X range + spawn line on resize
      // (Phaser 3.60+ accepts updating ops via setConfig; keeping it simple here)
      this.emitter.setConfig({
        x: { min: 0, max: w },
        y: h + 20,
      } as Phaser.Types.GameObjects.Particles.ParticleEmitterConfig);
    }
  }

  destroy(): void {
    this.scene.scale.off('resize', this.resizeBound);
    this.vignette?.destroy();
    this.emitter?.destroy();
    this.vignette = null;
    this.emitter = null;
  }
}
